import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Box, CircularProgress, Typography } from '@material-ui/core'
import axios from 'axios'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider,
} from './maricopaBasemap'

const STATUS_STYLES = {
  completed: { label: 'Completed', fill: '#2e7d32', stroke: '#1b5e20', opacity: 0.48 },
  remaining: { label: 'Remaining', fill: '#f9a825', stroke: '#f57f17', opacity: 0.42 },
  scoped: { label: 'Scoped', fill: '#1565c0', stroke: '#0d47a1', opacity: 0.32 },
  do_not_spray: { label: 'Do Not Spray', fill: '#78909c', stroke: '#b71c1c', opacity: 0.12, dashArray: '5,5' },
  split: { label: 'Completed Split', fill: '#2e7d32', stroke: '#1b5e20', opacity: 0.5, dashArray: '3,3' },
  remaining_split: { label: 'Remaining Split', fill: '#f9a825', stroke: '#f57f17', opacity: 0.42, dashArray: '5,3' }
}

const normalizePoint = (point = {}) => {
  const lat = Number(point.lat ?? point.latitude)
  const lng = Number(point.lng ?? point.lon ?? point.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const normalizePaths = (paths = []) => paths
  .map(normalizePoint)
  .filter(Boolean)

const getAreaId = (area = {}) => String(area._id || area.id || area.areaId || '')

const getAreaName = (area = {}) => area.name || area.displayName || area.areaName || 'Unnamed area'

const getCustomZoneId = (zone = {}, index = 0) => String(zone._id || zone.id || zone.areaId || `custom_zone_${index}`)

const getSplitAreaId = (splitArea = {}) => String(splitArea.splitAreaId || splitArea.sliceId || splitArea.id || splitArea._id || '')

const getSliceStatus = (slice, completedSplitAreas, remainingSplitAreas) => {
  const sliceId = String(slice.sliceId || '')
  if (completedSplitAreas.some(s => getSplitAreaId(s) === sliceId)) return 'completed'
  if (remainingSplitAreas.some(s => getSplitAreaId(s) === sliceId)) return 'remaining'
  return 'scoped'
}

const LegendItem = ({ status }) => {
  const style = STATUS_STYLES[status]
  return (
    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
      <span style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        background: style.fill,
        border: `1px solid ${style.stroke}`,
        opacity: style.opacity + 0.25
      }} />
      <Typography variant="caption" style={{ color: '#607d8b', fontSize: 10 }}>
        {style.label}
      </Typography>
    </Box>
  )
}

const SprayProgressMap = ({ url, scheduleItemId, jobsiteId, sprayScopeResolution, sprayProgress }) => {
  const [areas, setAreas] = useState([])
  const [slices, setSlices] = useState([])
  const [loading, setLoading] = useState(false)
  const [mapContainerNode, setMapContainerNode] = useState(null)
  const mapRef = useRef(null)
  const tileLayerRef = useRef(null)
  const polygonLayersRef = useRef([])

  const customZoneAreas = useMemo(
    () => (sprayScopeResolution?.sprayScopeSnapshot?.customZones || [])
      .map((zone, index) => ({
        _id: getCustomZoneId(zone, index),
        name: zone.label || `Custom Zone ${index + 1}`,
        areaType: 'custom_zone',
        paths: zone.paths || []
      })),
    [sprayScopeResolution?.sprayScopeSnapshot?.customZones]
  )
  const displayAreas = useMemo(
    () => [...areas, ...customZoneAreas],
    [areas, customZoneAreas]
  )
  const plannedAreaIds = useMemo(
    () => new Set([
      ...(sprayScopeResolution?.plannedAreaIds || []).map(String),
      ...customZoneAreas.map(getAreaId).filter(Boolean)
    ]),
    [customZoneAreas, sprayScopeResolution?.plannedAreaIds]
  )
  const completedAreaIds = useMemo(
    () => new Set((sprayProgress?.completedAreaIds || []).map(String)),
    [sprayProgress?.completedAreaIds]
  )
  const remainingAreaIds = useMemo(
    () => new Set((sprayProgress?.remainingAreaIds || []).map(String)),
    [sprayProgress?.remainingAreaIds]
  )
  const completedSplitAreas = useMemo(
    () => Array.isArray(sprayProgress?.completedSplitAreas) ? sprayProgress.completedSplitAreas : [],
    [sprayProgress?.completedSplitAreas]
  )
  const remainingSplitAreas = useMemo(
    () => Array.isArray(sprayProgress?.remainingSplitAreas) ? sprayProgress.remainingSplitAreas : [],
    [sprayProgress?.remainingSplitAreas]
  )
  const activeSlices = useMemo(
    () => slices.filter(s => s.status === 'available' || s.status === 'selected_for_load'),
    [slices]
  )
  const slicedRootAreaIds = useMemo(
    () => new Set(activeSlices.map(s => String(s.rootAreaId))),
    [activeSlices]
  )
  const hasSlices = activeSlices.length > 0

  const fetchAreas = useCallback(async () => {
    if (!scheduleItemId) return
    setLoading(true)
    try {
      const params = jobsiteId ? { jobsiteId } : {}
      const resp = await axios.get(`${url}/api/herbicide-schedule/${scheduleItemId}/areas`, { params })
      setAreas(resp.data.areas || [])
      setSlices(resp.data.slices || [])
    } catch (err) {
      console.error('Error fetching spray progress map areas:', err)
    } finally {
      setLoading(false)
    }
  }, [url, scheduleItemId, jobsiteId])

  useEffect(() => {
    fetchAreas()
  }, [fetchAreas])

  const visibleScopedAreas = useMemo(() => {
    if (!displayAreas.length) return []
    return displayAreas.filter(area => plannedAreaIds.size === 0 || plannedAreaIds.has(getAreaId(area)))
  }, [displayAreas, plannedAreaIds])

  const remainingAreas = useMemo(() => {
    const plannedById = new Map((sprayScopeResolution?.plannedAreas || []).map(area => [String(area._id), area]))
    return [...remainingAreaIds]
      .map(areaId => plannedById.get(String(areaId)) || displayAreas.find(area => getAreaId(area) === String(areaId)))
      .filter(Boolean)
  }, [displayAreas, remainingAreaIds, sprayScopeResolution?.plannedAreas])

  const handleMapContainerRef = useCallback((node) => {
    setMapContainerNode(node)
  }, [])

  useEffect(() => {
    if (loading || !mapContainerNode || !displayAreas.length) return

    if (mapRef.current) {
      try { mapRef.current.remove() } catch (_) {}
      mapRef.current = null
    }
    polygonLayersRef.current = []

    const allPaths = displayAreas.flatMap(area => normalizePaths(area.paths))
    const center = allPaths.length
      ? {
          lat: allPaths.reduce((sum, point) => sum + point.lat, 0) / allPaths.length,
          lng: allPaths.reduce((sum, point) => sum + point.lng, 0) / allPaths.length
        }
      : { lat: 33.505, lng: -112.15 }

    const map = L.map(mapContainerNode, {
      center: [center.lat, center.lng],
      zoom: allPaths.length ? 17 : 11,
      maxZoom: 28,
      zoomControl: true,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: false
    })

    applyBasemapProvider({
      map,
      tileLayerRef,
      provider: googleHybridProvider,
    })

    mapRef.current = map

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapRef.current) return
        applyBasemapProvider({
          map,
          tileLayerRef,
          provider,
        })
      })
      .catch((err) => {
        console.warn('Failed to load latest Maricopa basemap for spray progress map:', err)
      })

    // Draw polygons
    const bounds = L.latLngBounds([])

    displayAreas.forEach((area) => {
      const areaId = getAreaId(area)
      if (hasSlices && slicedRootAreaIds.has(areaId)) return
      const paths = normalizePaths(area.paths)
      if (paths.length < 3) return

      const isScoped = plannedAreaIds.size === 0 || plannedAreaIds.has(areaId)
      let status = isScoped ? 'scoped' : 'do_not_spray'
      if (completedAreaIds.has(areaId)) status = 'completed'
      else if (remainingAreaIds.has(areaId)) status = 'remaining'

      const statusStyle = STATUS_STYLES[status]
      const latlngs = paths.map(point => [point.lat, point.lng])
      bounds.extend(latlngs)

      const polygon = L.polygon(latlngs, {
        fillColor: statusStyle.fill,
        fillOpacity: statusStyle.opacity,
        color: statusStyle.stroke,
        weight: status === 'completed' ? 2.5 : 1.5,
        opacity: status === 'do_not_spray' ? 0.7 : 1,
        dashArray: statusStyle.dashArray || null,
        interactive: false,
      }).addTo(map)

      polygon.bindTooltip(`${getAreaName(area)} - ${statusStyle.label}`, {
        sticky: true,
        direction: 'top'
      })
      polygonLayersRef.current.push(polygon)
    })

    if (hasSlices) {
      activeSlices.forEach((slice) => {
        const paths = normalizePaths(slice.paths)
        if (paths.length < 3) return
        const status = getSliceStatus(slice, completedSplitAreas, remainingSplitAreas)
        const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.scoped
        const latlngs = paths.map(point => [point.lat, point.lng])
        bounds.extend(latlngs)

        const polygon = L.polygon(latlngs, {
          fillColor: statusStyle.fill,
          fillOpacity: statusStyle.opacity,
          color: statusStyle.stroke,
          weight: status === 'completed' ? 2.5 : 1.8,
          opacity: 1,
          dashArray: statusStyle.dashArray || null,
          interactive: false,
        }).addTo(map)

        polygon.bindTooltip(`${slice.name || slice.sliceId} - ${statusStyle.label}`, {
          sticky: true,
          direction: 'top'
        })
        polygonLayersRef.current.push(polygon)
      })
    } else {
      remainingSplitAreas.forEach((splitArea, index) => {
        const paths = normalizePaths(splitArea.paths || splitArea.points || splitArea.coordinates)
        if (paths.length < 3) return
        const latlngs = paths.map(point => [point.lat, point.lng])
        bounds.extend(latlngs)
        const statusStyle = STATUS_STYLES.remaining_split
        const polygon = L.polygon(latlngs, {
          fillColor: statusStyle.fill,
          fillOpacity: statusStyle.opacity,
          color: statusStyle.stroke,
          weight: 1.8,
          dashArray: statusStyle.dashArray,
          interactive: false,
        }).addTo(map)
        polygon.bindTooltip(`${splitArea.name || `Remaining split ${index + 1}`} - Remaining Split`, {
          sticky: true,
          direction: 'top'
        })
        polygonLayersRef.current.push(polygon)
      })

      completedSplitAreas.forEach((splitArea, index) => {
        const paths = normalizePaths(splitArea.paths || splitArea.points || splitArea.coordinates)
        if (paths.length < 3) return
        const latlngs = paths.map(point => [point.lat, point.lng])
        bounds.extend(latlngs)
        const statusStyle = STATUS_STYLES.split
        const polygon = L.polygon(latlngs, {
          fillColor: statusStyle.fill,
          fillOpacity: statusStyle.opacity,
          color: statusStyle.stroke,
          weight: 2,
          dashArray: statusStyle.dashArray,
          interactive: false,
        }).addTo(map)
        polygon.bindTooltip(`${splitArea.name || `Split ${index + 1}`} - Completed Split`, {
          sticky: true,
          direction: 'top'
        })
        polygonLayersRef.current.push(polygon)
      })
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [16, 16] })
    }

    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
    }, 150)

    return () => {
      cancelled = true
      polygonLayersRef.current = []
      if (tileLayerRef.current) {
        try { map.removeLayer(tileLayerRef.current) } catch (_) {}
        tileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapRef.current = null
    }
  }, [displayAreas, loading, mapContainerNode, plannedAreaIds, completedAreaIds, remainingAreaIds, completedSplitAreas, remainingSplitAreas, activeSlices, hasSlices, slicedRootAreaIds])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  if (!displayAreas.length) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
          No mapped areas are available for progress display.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <div ref={handleMapContainerRef} style={{ width: '100%', height: 220, borderRadius: 4, overflow: 'hidden' }} />
      <Box display="flex" alignItems="center" flexWrap="wrap" mt={1} style={{ gap: 10 }}>
        <LegendItem status="remaining" />
        <LegendItem status="completed" />
        <LegendItem status="split" />
        <LegendItem status="remaining_split" />
        <LegendItem status="do_not_spray" />
      </Box>
      <Box mt={1}>
        <Typography variant="caption" style={{ color: '#607d8b', display: 'block' }}>
          {sprayProgress?.completedAreaCount || 0}/{sprayProgress?.plannedAreaCount || visibleScopedAreas.length} scoped areas complete
          {sprayProgress?.remainingAreaCount != null ? ` - ${sprayProgress.remainingAreaCount} remaining` : ''}
        </Typography>
        {remainingAreas.length > 0 && (
          <Typography variant="caption" style={{ color: '#455a64', display: 'block', marginTop: 4 }}>
            Remaining: {remainingAreas.map(getAreaName).join(', ')}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default SprayProgressMap
